/**
 * src/hexatone/user-tunings.js
 *
 * LocalStorage-backed user tuning library helpers.
 *
 * This module owns persistence for the user-editable tuning library: loading,
 * saving, overwrite/upsert, deletion, clear-all, duplicate-name handling, and
 * parsing imported tuning JSON into normalized tuning records.
 */
import { findPresetTuningByName } from "./preset-tunings/index.js";
import { normalizeTuningRecord } from "./tuning-record.js";

export const USER_TUNINGS_STORAGE_KEY = "hexatone_custom_presets";

export function loadUserTunings() {
  try {
    const raw = localStorage.getItem(USER_TUNINGS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.map((record) => normalizeTuningRecord(record)).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

export function saveUserTunings(records) {
  const normalized = Array.isArray(records)
    ? records.map((record) => normalizeTuningRecord(record)).filter(Boolean)
    : [];
  localStorage.setItem(USER_TUNINGS_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

export function upsertUserTuning(record, existing = loadUserTunings()) {
  const normalized = normalizeTuningRecord(record);
  if (!normalized) return existing;
  const next = existing.some((entry) => entry.name === normalized.name)
    ? existing.map((entry) => (entry.name === normalized.name ? normalized : entry))
    : [...existing, normalized];
  return saveUserTunings(next);
}

export function deleteUserTuning(name, existing = loadUserTunings()) {
  const trimmed = String(name ?? "").trim();
  const next = existing.filter((entry) => entry.name !== trimmed);
  return saveUserTunings(next);
}

export function clearUserTunings() {
  localStorage.setItem(USER_TUNINGS_STORAGE_KEY, JSON.stringify([]));
  return [];
}

export function uniqueTuningName(baseName, existing = loadUserTunings()) {
  const rawBase = String(baseName ?? "").trim() || "User Tuning";
  const match = rawBase.match(/^(.*?)(?:\s+(\d+))?$/);
  const stem = String(match?.[1] ?? rawBase).trim() || "User Tuning";
  const startingSuffix = Number.parseInt(match?.[2] ?? "", 10);
  const taken = new Set(existing.map((entry) => entry.name));
  if (findPresetTuningByName(rawBase)) taken.add(rawBase);
  if (!taken.has(rawBase) && !findPresetTuningByName(rawBase)) return rawBase;
  let suffix = Number.isFinite(startingSuffix) ? startingSuffix + 1 : 2;
  while (taken.has(`${stem} ${suffix}`) || findPresetTuningByName(`${stem} ${suffix}`)) {
    suffix += 1;
  }
  return `${stem} ${suffix}`;
}

export function parseTuningJson(text) {
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map((record) => normalizeTuningRecord(record)).filter(Boolean);
    }
    const normalized = normalizeTuningRecord(parsed);
    return normalized ? [normalized] : [];
  } catch {
    return [];
  }
}
